import { plain } from '../../lib/labels';
import { useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Modal,
  Input,
  InputNumber,
  Switch,
  Popconfirm,
  Alert,
  App,
  Select,
  Upload,
  Tooltip,
  type TableColumnsType,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  PoweroffOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { formatRupees } from '../../lib/money';
import { giftPricingError, giftPricingFromRow, giftPricingToWire } from './giftForm';

type GiftKind = 'STANDARD' | 'FACE_AR';
type FaceAnchor = 'GLASSES' | 'CROWN' | 'EARRINGS' | 'NOSE' | 'FACE' | 'LIPS' | 'KISS_MARK';
type DeviceTier = 'LOW' | 'MID' | 'HIGH';
type GiftContext = 'call' | 'live' | 'party';

interface AdminGift {
  id: string;
  code: string;
  name: string;
  emoji: string | null;
  priceCoins: number;
  priceLabel: string; // "99 coins"
  /** What the listener earns per gift, in paise. Independent of priceCoins. */
  listenerEarningPaise?: number | string | null;
  displayOrder: number;
  isActive: boolean;
  contexts: GiftContext[];
  animationKey: string | null;
  kind: GiftKind;
  effectAnchor: FaceAnchor | null;
  effectAssetKey: string | null;
  effectDurationSeconds: number | null;
  minDeviceTier: DeviceTier;
  /** Null for a standard gift; for a face-AR gift, whether the effect bundle
   *  is actually in storage. A face-AR gift with no bundle is invisible to
   *  every user — this is the only screen that says so. */
  bundleUploaded: boolean | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Anchors that are derived from the six-point face pose and need no landmark
 * contour, so any handset can wear them. LIPS and KISS_MARK need a contour and
 * are therefore only offered to devices that reported one.
 */
const CONTOURLESS_ANCHORS: FaceAnchor[] = ['GLASSES', 'CROWN', 'EARRINGS', 'NOSE', 'FACE'];

const ANCHOR_HELP: Record<FaceAnchor, string> = {
  GLASSES: 'Across the eyes — sunglasses, spectacles.',
  CROWN: 'Above the forehead — crowns, hats, halos.',
  EARRINGS: 'Either side of the head.',
  NOSE: 'Bridge and tip — whiskers, a moustache, a cat nose.',
  FACE: 'The whole face as one surface — glitter, freckles, a full mask.',
  LIPS: 'Follows the mouth contour. Needs a device that reports one.',
  KISS_MARK: 'Placed on the cheek from the mouth contour. Needs a contour.',
};

interface GiftForm {
  code: string;
  name: string;
  emoji: string;
  priceCoins: number;
  /** Entered in ₹; sent as integer `listenerEarningPaise`. */
  listenerEarningRupees: number;
  displayOrder: number;
  isActive: boolean;
  contexts: GiftContext[];
  kind: GiftKind;
  effectAnchor: FaceAnchor | null;
  effectAssetKey: string;
  effectDurationSeconds: number;
  minDeviceTier: DeviceTier;
}

const EMPTY: GiftForm = {
  code: '',
  name: '',
  emoji: '',
  priceCoins: 99,
  listenerEarningRupees: 0,
  displayOrder: 0,
  isActive: true,
  contexts: [],
  kind: 'STANDARD',
  effectAnchor: null,
  effectAssetKey: '',
  effectDurationSeconds: 15,
  minDeviceTier: 'LOW',
};

function errMsg(e: unknown, fallback: string) {
  return (
    (e as { response?: { data?: { message?: string } } }).response?.data
      ?.message ?? fallback
  );
}

export function GiftsPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AdminGift | null>(null);
  const [form, setForm] = useState<GiftForm>(EMPTY);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'gifts'],
    queryFn: async () =>
      (await api.get<{ gifts: AdminGift[] }>('/admin/gifts')).data.gifts,
  });

  const save = useMutation({
    mutationFn: async () => {
      const isAr = form.kind === 'FACE_AR';
      const body = {
        code: form.code.trim().toLowerCase(),
        name: form.name.trim(),
        emoji: form.emoji.trim() || null,
        // Sender price (coins) and listener earning (₹ → paise) are independent.
        ...giftPricingToWire(form),
        displayOrder: form.displayOrder,
        isActive: form.isActive,
        contexts: form.contexts,
        kind: form.kind,
        // A standard gift carries no effect fields at all — sending stale ones
        // would leave an anchor on a gift that no longer hangs anywhere.
        effectAnchor: isAr ? form.effectAnchor : null,
        effectAssetKey: isAr ? form.effectAssetKey.trim() || null : null,
        effectDurationSeconds: isAr ? form.effectDurationSeconds : null,
        minDeviceTier: isAr ? form.minDeviceTier : 'LOW',
      };
      if (editing) await api.put(`/admin/gifts/${editing.id}`, body);
      else await api.post('/admin/gifts', body);
    },
    onSuccess: () => {
      message.success(editing ? 'Gift updated' : 'Gift created');
      qc.invalidateQueries({ queryKey: ['admin', 'gifts'] });
      setOpen(false);
    },
    onError: (e) => message.error(errMsg(e, 'Could not save gift')),
  });

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.post(`/admin/gifts/${id}/${active ? 'activate' : 'deactivate'}`),
    onSuccess: () => {
      message.success('Updated');
      qc.invalidateQueries({ queryKey: ['admin', 'gifts'] });
    },
    onError: (e) => message.error(errMsg(e, 'Could not update')),
  });

  /**
   * Upload an effect bundle straight to storage.
   *
   * The server mints a short-lived PUT URL and DERIVES the key — we never
   * choose it. The bytes go browser → storage without passing through the API.
   * On success we drop the returned key into the form; the gift is not sellable
   * until that key is saved on the row.
   */
  const uploadBundle = useMutation({
    mutationFn: async (file: File) => {
      const code = form.code.trim().toLowerCase();
      if (!code) throw new Error('Give the gift a code first — the key is derived from it.');
      const { data: minted } = await api.post<{ key: string; url: string }>(
        '/admin/gifts/effect-bundle/upload-url',
        { code },
      );
      const res = await fetch(minted.url, {
        method: 'PUT',
        headers: { 'content-type': 'application/zip' },
        body: file,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      return minted.key;
    },
    onSuccess: (key) => {
      setForm((f) => ({ ...f, effectAssetKey: key }));
      message.success('Bundle uploaded — save the gift to publish it');
    },
    onError: (e) =>
      message.error(
        e instanceof Error ? e.message : errMsg(e, 'Could not upload the effect bundle'),
      ),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = (row: AdminGift) => {
    setEditing(row);
    setForm({
      code: row.code,
      name: row.name,
      emoji: row.emoji ?? '',
      ...giftPricingFromRow(row),
      displayOrder: row.displayOrder,
      isActive: row.isActive,
      contexts: row.contexts ?? [],
      kind: row.kind ?? 'STANDARD',
      effectAnchor: row.effectAnchor,
      effectAssetKey: row.effectAssetKey ?? '',
      effectDurationSeconds: row.effectDurationSeconds ?? 15,
      minDeviceTier: row.minDeviceTier ?? 'LOW',
    });
    setOpen(true);
  };

  const columns: TableColumnsType<AdminGift> = [
    {
      title: 'Gift',
      render: (_: unknown, row) => (
        <Space>
          <span style={{ fontSize: 20 }}>{row.emoji ?? '🎁'}</span>
          <Space direction="vertical" size={0}>
            <span style={{ fontWeight: 600 }}>{row.name}</span>
            <code style={{ fontSize: 11, color: '#8B8B8B' }}>{row.code}</code>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Type',
      width: 190,
      render: (_: unknown, row) =>
        row.kind === 'FACE_AR' ? (
          <Space direction="vertical" size={2}>
            <Space size={4}>
              <Tag color="magenta">face AR</Tag>
              <Tag>{plain(row.effectAnchor)}</Tag>
            </Space>
            {row.bundleUploaded === false ? (
              <Tooltip title="The effect bundle named by this gift is not in storage. Nobody can see this gift — a face-AR gift has no emoji fallback, so it is hidden rather than shown broken.">
                <Tag color="error" icon={<WarningOutlined />}>
                  no bundle — hidden
                </Tag>
              </Tooltip>
            ) : (
              <span style={{ fontSize: 11, color: '#8B8B8B' }}>
                {row.effectDurationSeconds}s · min tier {row.minDeviceTier}
              </span>
            )}
          </Space>
        ) : (
          <Tag>standard</Tag>
        ),
    },
    {
      title: 'Where',
      width: 140,
      render: (_: unknown, row) =>
        row.contexts && row.contexts.length > 0 ? (
          <Space size={4} wrap>
            {row.contexts.map((c) => (
              <Tag key={c}>{c}</Tag>
            ))}
          </Space>
        ) : (
          <Tooltip title="No restriction — sellable on calls, live rooms and parties.">
            <Tag color="blue">everywhere</Tag>
          </Tooltip>
        ),
    },
    {
      title: 'Price',
      dataIndex: 'priceCoins',
      render: (c: number) => (
        <Tag color="gold">{c.toLocaleString('en-IN')} coins</Tag>
      ),
    },
    {
      title: 'Listener earns',
      dataIndex: 'listenerEarningPaise',
      render: (p: number | string | null | undefined) =>
        p === null || p === undefined ? (
          <Tag color="warning">not set</Tag>
        ) : (
          <Tag color="green">{formatRupees(p)}</Tag>
        ),
    },
    { title: 'Order', dataIndex: 'displayOrder', width: 70 },
    {
      title: 'Active',
      render: (_: unknown, row) =>
        row.isActive ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            active
          </Tag>
        ) : (
          <Tag>inactive</Tag>
        ),
    },
    {
      title: 'Actions',
      render: (_: unknown, row) => (
        <Space>
          <Can permission="gifts.edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>
              Edit
            </Button>
          </Can>
          <Can permission="gifts.toggle">
            {row.isActive ? (
            <Popconfirm
              title="Hide this gift?"
              description="It will disappear from the in-call gift picker."
              onConfirm={() => setActive.mutate({ id: row.id, active: false })}
              okButtonProps={{ danger: true }}
            >
              <Button size="small" icon={<PoweroffOutlined />}>
                Deactivate
              </Button>
            </Popconfirm>
          ) : (
            <Button
              size="small"
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => setActive.mutate({ id: row.id, active: true })}
            >
              Activate
            </Button>
          )}
          </Can>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
          Gift catalog
        </Typography.Title>
        <Can permission="gifts.create">
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New gift
          </Button>
        </Can>
      </div>
      <Alert
        showIcon
        type="info"
        message="Gifts are spent from the coin wallet"
        description="The sender pays the price in coins (never charged to a card). The listener earns the ₹ amount you set per gift — the two are configured independently; nothing is split or derived."
      />
      <Card>
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          pagination={{ pageSize: 25 }}
        />
      </Card>

      <Modal
        title={editing ? `Edit gift — ${editing.name}` : 'New gift'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => save.mutate()}
        okText={editing ? 'Save' : 'Create'}
        okButtonProps={{
          disabled:
            form.name.trim().length < 1 ||
            form.code.trim().length < 1 ||
            giftPricingError(form) !== null ||
            // Mirrors the server's rule, so an incomplete face-AR row is
            // refused here rather than bouncing off the API.
            (form.kind === 'FACE_AR' &&
              (!form.effectAnchor ||
                form.effectAssetKey.trim().length < 1 ||
                form.effectDurationSeconds < 1)),
          loading: save.isPending,
        }}
      >
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ width: 90 }}>
              <Typography.Text>Emoji</Typography.Text>
              <Input
                style={{ marginTop: 4, textAlign: 'center', fontSize: 18 }}
                placeholder="🌹"
                maxLength={8}
                value={form.emoji}
                onChange={(e) => setForm({ ...form, emoji: e.target.value })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Typography.Text>Name</Typography.Text>
              <Input
                style={{ marginTop: 4 }}
                placeholder="Rose"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Typography.Text>
              Code <span style={{ color: '#8B8B8B' }}>(lowercase slug, unique)</span>
            </Typography.Text>
            <Input
              style={{ marginTop: 4 }}
              placeholder="rose"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
            />
            {editing && (
              <Typography.Text style={{ fontSize: 11, color: '#C58B00' }}>
                Already-sent gifts keep their old code — changing this only
                affects future sends.
              </Typography.Text>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Typography.Text>Price (coins)</Typography.Text>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                min={1}
                max={10_000_000}
                precision={0}
                value={form.priceCoins}
                onChange={(v) => setForm({ ...form, priceCoins: v ?? 1 })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Typography.Text>Listener earns (₹)</Typography.Text>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                min={0}
                max={1_000_000}
                precision={2}
                prefix="₹"
                value={form.listenerEarningRupees}
                onChange={(v) => setForm({ ...form, listenerEarningRupees: v ?? 0 })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Typography.Text>Display order</Typography.Text>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                min={0}
                max={9999}
                value={form.displayOrder}
                onChange={(v) => setForm({ ...form, displayOrder: v ?? 0 })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Typography.Text style={{ display: 'block' }}>Active</Typography.Text>
              <Switch
                style={{ marginTop: 8 }}
                checked={form.isActive}
                onChange={(v) => setForm({ ...form, isActive: v })}
              />
            </div>
          </div>

          <div>
            <Typography.Text>
              Sellable on{' '}
              <span style={{ color: '#8B8B8B' }}>(leave empty for everywhere)</span>
            </Typography.Text>
            <Select
              mode="multiple"
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              placeholder="everywhere"
              value={form.contexts}
              onChange={(v: GiftContext[]) => setForm({ ...form, contexts: v })}
              options={[
                { value: 'call', label: '1:1 calls' },
                { value: 'live', label: 'Live rooms' },
                { value: 'party', label: 'Parties' },
              ]}
            />
          </div>

          <div>
            <Typography.Text>Kind</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              value={form.kind}
              onChange={(v: GiftKind) => setForm({ ...form, kind: v })}
              options={[
                { value: 'STANDARD', label: 'Standard — a 2-D animation over the video' },
                { value: 'FACE_AR', label: "Face AR — worn on the recipient's face" },
              ]}
            />
          </div>

          {form.kind === 'FACE_AR' && (
            <Card size="small" style={{ background: 'rgba(255,0,128,0.04)' }}>
              <Space direction="vertical" size={14} style={{ width: '100%' }}>
                <Alert
                  showIcon
                  type="warning"
                  message="A face-AR gift with no bundle is invisible"
                  description="Unlike a standard gift, there is no emoji to fall back to — so a gift whose bundle is missing is hidden from every picker rather than shown broken. You can save the row before uploading; it simply will not be sellable until the file lands."
                />

                <div>
                  <Typography.Text>Anchor — where it sits on the face</Typography.Text>
                  <Select
                    style={{ width: '100%', marginTop: 4 }}
                    placeholder="Choose an anchor"
                    value={form.effectAnchor ?? undefined}
                    onChange={(v: FaceAnchor) =>
                      setForm({
                        ...form,
                        effectAnchor: v,
                        // LIPS and KISS_MARK need a landmark contour, which is a
                        // MID-tier capability. Snapping the floor up here stops
                        // an author shipping a gift that silently renders for
                        // nobody.
                        minDeviceTier: CONTOURLESS_ANCHORS.includes(v)
                          ? form.minDeviceTier
                          : form.minDeviceTier === 'LOW'
                            ? 'MID'
                            : form.minDeviceTier,
                      })
                    }
                    options={(Object.keys(ANCHOR_HELP) as FaceAnchor[]).map((a) => ({
                      value: a,
                      label: `${a} — ${ANCHOR_HELP[a]}`,
                    }))}
                  />
                </div>

                <div>
                  <Typography.Text>Effect bundle</Typography.Text>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <Input
                      readOnly
                      placeholder="Upload a .zip to fill this in"
                      value={form.effectAssetKey}
                    />
                    <Upload
                      accept=".zip,application/zip"
                      showUploadList={false}
                      beforeUpload={(file) => {
                        uploadBundle.mutate(file);
                        return false; // we PUT it ourselves, to storage
                      }}
                    >
                      <Button icon={<UploadOutlined />} loading={uploadBundle.isPending}>
                        Upload
                      </Button>
                    </Upload>
                  </div>
                  <Typography.Text style={{ fontSize: 11, color: '#8B8B8B' }}>
                    A zip of the PNG atlas and its anchor manifest. The storage key is
                    derived from the gift code — set the code first.
                  </Typography.Text>
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <Typography.Text>Duration (seconds)</Typography.Text>
                    <InputNumber
                      style={{ width: '100%', marginTop: 4 }}
                      min={1}
                      max={120}
                      value={form.effectDurationSeconds}
                      onChange={(v) => setForm({ ...form, effectDurationSeconds: v ?? 15 })}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Typography.Text>Minimum device tier</Typography.Text>
                    <Select
                      style={{ width: '100%', marginTop: 4 }}
                      value={form.minDeviceTier}
                      onChange={(v: DeviceTier) => setForm({ ...form, minDeviceTier: v })}
                      options={[
                        { value: 'LOW', label: 'LOW — any handset' },
                        { value: 'MID', label: 'MID — needs a landmark contour' },
                        { value: 'HIGH', label: 'HIGH — fastest devices only' },
                      ]}
                      disabled={
                        !!form.effectAnchor && !CONTOURLESS_ANCHORS.includes(form.effectAnchor)
                      }
                    />
                    {!!form.effectAnchor && !CONTOURLESS_ANCHORS.includes(form.effectAnchor) && (
                      <Typography.Text style={{ fontSize: 11, color: '#C58B00' }}>
                        {form.effectAnchor} needs a face contour, so it cannot be sold to a
                        LOW-tier device.
                      </Typography.Text>
                    )}
                  </div>
                </div>
              </Space>
            </Card>
          )}
        </Space>
      </Modal>
    </Space>
  );
}
